/**
 * Localized routing — the locale lives in the URL, and so do the path segments.
 *
 *     /pt-br/ordens-servico        /es-cl/ordenes-servicio
 *     /pt-br/ordens-servico/4821   /es-cl/ordenes-servicio/4821
 *
 * Two things follow from that, and both are load-bearing.
 *
 * **The URL is the source of truth for what language the page renders in.** Not
 * `navigator.language`, not the stored preference. A customer who sends a link
 * to someone must send the language too, and a page must not be able to
 * disagree with its own address bar.
 *
 * **Slugs are quasi-permanent.** A UI string can be reworded in a patch release;
 * a slug that changes breaks every bookmark and every deep link already sent to
 * a phone. Treat this file as closer to a database migration than to a
 * catalogue — see ADR-0015.
 *
 * ⚠️ This does NOT contradict CLAUDE.md §1's "do not translate them" rule. That
 *    rule governs code identifiers, API field names, database columns and
 *    contract payloads, which stay Portuguese forever. `RouteId` below is
 *    exactly that: `ordensServico` is the identifier, in Portuguese, and it is
 *    what the rest of the app refers to. Only the rendered slug is translated,
 *    for the same reason the rendered UI is — the Chilean customer heard
 *    *órdenes de servicio* at the counter.
 */
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isSupportedLocale, type Locale } from './index.ts';

/**
 * The URL segment for each locale — lowercase, because a path segment that
 * changes case is a different URL to a cache and to a customer typing it.
 */
const LOCALE_PREFIX: Record<Locale, string> = {
  'pt-BR': 'pt-br',
  'es-CL': 'es-cl',
  'es-EC': 'es-ec',
};

export function prefixFor(locale: Locale): string {
  return LOCALE_PREFIX[locale];
}

/** The canonical route identifiers. Portuguese, and never rendered. */
export type RouteId =
  | 'login'
  | 'rotate'
  | 'ordensServico'
  | 'ordemServicoDetalhe'
  | 'laudo'
  | 'notificacoes';

/**
 * The translated slug for each route.
 *
 * `ordemServicoDetalhe` has no entry of its own: a detail page is the list slug
 * plus an id, so it can never drift from its parent.
 */
const SLUGS: Record<Exclude<RouteId, 'ordemServicoDetalhe'>, Record<Locale, string>> = {
  login: { 'pt-BR': 'entrar', 'es-CL': 'ingresar', 'es-EC': 'ingresar' },
  rotate: { 'pt-BR': 'nova-senha', 'es-CL': 'nueva-clave', 'es-EC': 'nueva-contrasena' },
  ordensServico: { 'pt-BR': 'ordens-servico', 'es-CL': 'ordenes-servicio', 'es-EC': 'ordenes-servicio' },
  laudo: { 'pt-BR': 'laudo', 'es-CL': 'informe', 'es-EC': 'informe' },
  notificacoes: { 'pt-BR': 'notificacoes', 'es-CL': 'notificaciones', 'es-EC': 'notificaciones' },
};

export function slugFor(routeId: Exclude<RouteId, 'ordemServicoDetalhe'>, locale: Locale): string {
  return SLUGS[routeId][locale];
}

/**
 * Params a route needs to be buildable. The detail page takes `osId`; the
 * laudo viewer takes all three — `tipo` is the WIRE enum (`entry` | `exit`,
 * `docs/handoff/2026-07-30-read-endpoints-shipped.md`), a payload value in the
 * URL like the ids, never a translated slug.
 */
export interface RouteParams {
  osId?: string | undefined;
  itemId?: string | undefined;
  tipo?: string | undefined;
}

/**
 * The one way to produce a URL in this app. Nothing should concatenate a path
 * by hand — that is how a locale prefix goes missing on exactly one code path
 * and nobody notices until a customer is stranded on a 404.
 */
export function buildPath(routeId: RouteId, locale: Locale, params?: RouteParams): string {
  const p = prefixFor(locale);

  if (routeId === 'ordemServicoDetalhe') {
    const list = SLUGS.ordensServico[locale];
    // A detail path without an id is the list — better than emitting `/undefined`.
    return params?.osId ? `/${p}/${list}/${params.osId}` : `/${p}/${list}`;
  }

  if (routeId === 'laudo') {
    const list = SLUGS.ordensServico[locale];
    // Without the full triplet there is no report to address — degrade to the
    // nearest page that exists (the order, or the list), never `/undefined`.
    if (params?.osId && params?.itemId && params?.tipo) {
      return `/${p}/${list}/${params.osId}/${SLUGS.laudo[locale]}/${params.itemId}/${params.tipo}`;
    }
    return params?.osId ? `/${p}/${list}/${params.osId}` : `/${p}/${list}`;
  }

  return `/${p}/${SLUGS[routeId][locale]}`;
}

/** Where a customer with no more specific destination should land. */
export function homePath(locale: Locale): string {
  return buildPath('ordensServico', locale);
}

/**
 * The locale a pathname is asking for, or null if it carries no valid prefix.
 *
 * Used before the first paint so the correct catalogue is the one that loads,
 * rather than loading the detected one and swapping a tick later.
 */
export function localeFromPathname(pathname: string): Locale | null {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  if (!first) return null;

  const match = SUPPORTED_LOCALES.find((l) => LOCALE_PREFIX[l] === first);
  return match ?? null;
}

/**
 * The same page, in another language.
 *
 * Switching locale must keep the customer where they are — dropping them on the
 * home page because they changed language is the kind of thing that makes a
 * language selector feel broken. Params are carried across; the slug is not,
 * because it is translated.
 */
export function translatePath(routeId: RouteId | undefined, to: Locale, params?: RouteParams): string {
  return routeId ? buildPath(routeId, to, params) : homePath(to);
}

/**
 * Legacy, unprefixed paths kept alive as permanent redirectors.
 *
 * They are also the link format anything server-side should emit. The BFF
 * composes push payloads (ADR-0006) and would otherwise need its own copy of
 * the slug table above, which would drift the first time a slug changed. A
 * locale-agnostic path that redirects costs one hop and keeps the table in one
 * place.
 */
export const LEGACY_PATHS: ReadonlyArray<{ path: string; routeId: RouteId }> = [
  { path: '/login', routeId: 'login' },
  { path: '/rotate', routeId: 'rotate' },
  { path: '/ordens-servico', routeId: 'ordensServico' },
  { path: '/ordens-servico/:osId', routeId: 'ordemServicoDetalhe' },
  // The deep-link format app-api-service emits for laudo notifications
  // (Portal_notification_php_service::deepLinkForEntryReport) — `:tipo` is the
  // wire enum ('entry' | 'exit'), never a translated slug.
  { path: '/ordens-servico/:osId/laudo/:itemId/:tipo', routeId: 'laudo' },
  { path: '/notificacoes', routeId: 'notificacoes' },
];

/** Narrowing helper for the value read off a route `handle`. */
export function isRouteId(value: unknown): value is RouteId {
  return (
    value === 'login' ||
    value === 'rotate' ||
    value === 'ordensServico' ||
    value === 'ordemServicoDetalhe' ||
    value === 'laudo' ||
    value === 'notificacoes'
  );
}

/** Re-exported so callers need one import for "what locale am I building for". */
export { DEFAULT_LOCALE, isSupportedLocale };
